import {
  observable,
  computed,
  action,
  makeObservable,
  runInAction,
} from "mobx";
import nanoid from "nanoid/non-secure";
import ProfileApi from "../api/ProfileApi";
import DocsApi from "../api/DocsApi";

import callWindowFunction from "../utils/callWindowFunction";
import sendUserTypeUpdateMessage from "../utils/sendUserTypeUpdateMessage";
import setCookie from "../utils/setCookie";
import getCookie from "../utils/getCookie";

import Counters from "./models/Counters";
import PayedServices from "./models/Licenses/PayedServices";

import { userTypes, LS_KEYS, MAIN_PAGE_QUERY_PARAM_STRING } from "../consts";

import RefreshBalance from "./models/Refresh/RefreshBalanse";
import RefreshLicenses from "./models/Refresh/RefreshLicenses";

class Profile {
  /** API для работы с текущим пользователем */
  currentUser;

  loginPopup = false;

  loginPopupNext = null;

  registrationPopup = false;

  registrationPopupNext = null;

  profileHasBeenReceived = false;

  /** Флаг, установлена ли кука для попапа регистрации */
  registrationPopupCookie = getCookie("reg_popup");

  /** Юзер сайта */
  user = {};

  /** Флаг о том, что нет профиля */
  noProfile = false;

  showLoginButtons = false;

  /** Структура Counters */
  counters;

  /** Структура PayedServices */
  payedServices;

  /** Структура RefreshBalance */
  refreshBalance;

  /** Структура RefreshLicenses */
  refreshLicenses;

  /** Структура RefreshLicenses (копия refreshLicenses, нужно для независимости кнопок обновления) */
  refreshUnlimitedLicenses;

  docsCounters = {};

  /** api для работа с ати-доками */
  docsApi;

  /** Дополнительные query параметры для iframe логин попапа */
  loginPopupAdditionalQueryParams = {};

  /** Дополнительные query параметры для iframe попапа регистрации */
  registrationPopupAdditionalQueryParams = {};

  constructor(rootStore, profileUrl, baseUrl) {
    makeObservable(this, {
      loginPopup: observable,
      loginPopupNext: observable,
      registrationPopup: observable,
      registrationPopupNext: observable,
      profileHasBeenReceived: observable,
      registrationPopupCookie: observable,
      docsCounters: observable,
      user: observable,
      noProfile: observable,
      showLoginButtons: observable,
      counters: observable,
      payedServices: observable,
      loginPopupAdditionalQueryParams: observable,
      registrationPopupAdditionalQueryParams: observable,
      setRegistrationPopupCookie: action,
      changeCurrentUser: action,
      onLoginOpen: action,
      onLoginClose: action,
      onRegistrationOpen: action,
      onRegistrationClose: action,
      setShowLoginButtons: action,
      getDocsCounters: action,
      setUser: action,
      isLoggedIn: computed,
      setNoProfile: action,
      userId: computed,
      isPaidUser: computed,
    });

    this.rootStore = rootStore;
    this.profileUrl = profileUrl;

    this.counters = new Counters(this, this.rootStore);
    this.payedServices = new PayedServices(this);

    if (process.env.NODE_ENV !== "static") {
      this.currentUser = new ProfileApi(profileUrl);
    }

    this.docsApi = new DocsApi(baseUrl);

    // Запрос профиля для локального запуска через npm run stage-env
    if (process.env.STAGE_ENV) {
      window.AtiUserData_AppSettings.getProfileInfo({ isForced: true }).then(
        (user) => {
          this.user.profile = user;
        }
      );
    }

    this.profileInfoPromiseHandler = null;
    this.retryProfileCounter = 0;
    this.retryProfileTimeout = null;
    this.tabId = nanoid();
    this.group = "header";

    window.changeCurrentUser = this.changeCurrentUser;
    window.setRegistrationPopupCookie = this.setRegistrationPopupCookie;
    window.openLoginPopup = this.onLoginOpen;
  }

  // функция для тестировщиков
  // используется чтобы переключать состояния шапки вручную
  changeCurrentUser = (profile, params) => {
    this.setUser(profile);

    if (params && params.isNoProfile !== undefined) {
      this.setNoProfile(params.isNoProfile);
    }

    if (params && params.showLoginButtons !== undefined) {
      this.setShowLoginButtons(params.showLoginButtons);
    }
  };

  /** Функция, которая устанавливает куку для открытия
   * регистрации в попапе */
  setRegistrationPopupCookie = () => {
    if (!this.registrationPopupCookie) {
      setCookie("reg_popup", true, {
        domain: window.location.hostname.replace(/.*(.ati\.s.*)/g, "$1"),
        expires: 30,
      });

      this.registrationPopupCookie = "true";
    }
  };

  getDocsCounters = () => {
    this.docsApi
      .getCounters()
      .then((data) => {
        runInAction(() => {
          this.docsCounters = data.data;
        });
      })
      .catch(() => {
        console.error("Error getting docs counters");
      });
  };

  loadProfile = ({
    callbackForUser,
    callbackForAnonim,
    shouldGetProfileByRequest,
  }) =>
    window.AtiUserData_AppSettings &&
    window.AtiUserData_AppSettings.getProfileInfo({
      isForced: shouldGetProfileByRequest,
    })
      .then((data) => {
        this.retryProfileCounter = 0;

        if (data.ati_code) {
          if (
            data.contact &&
            data.contact.has_personal_data_consent === false
          ) {
            window.location.href = `https://id.${this.rootStore.baseUrl}/confirm`;
          }

          this.setShowLoginButtons(false);
          this.setUser(data);

          this.refreshBalance = new RefreshBalance(this, this.profileUrl);
          this.refreshLicenses = new RefreshLicenses(this, this.profileUrl);
          this.refreshUnlimitedLicenses = new RefreshLicenses(
            this,
            this.profileUrl
          );

          sendUserTypeUpdateMessage(userTypes.USER, data.ati_code);
        }

        if (callbackForUser) {
          callbackForUser();
        }
      })
      .catch((data) => {
        if (!data) {
          if (this.retryProfileCounter < 5) {
            this.retryProfileCounter++;
            setTimeout(
              () =>
                this.loadProfile({
                  callbackForUser,
                  callbackForAnonim,
                  shouldGetProfileByRequest,
                }),
              2000
            );
          } else {
            this.retryProfileCounter = 0;

            this.setUser({});
            sendUserTypeUpdateMessage(userTypes.NO_PROFILE);
            this.setNoProfile(true);
          }
        } else if (data.unauthorized) {
          this.retryProfileCounter = 0;

          this.setShowLoginButtons(true);
          this.setUser({});
          sendUserTypeUpdateMessage(userTypes.UNAUTHORIZED);

          if (callbackForAnonim) {
            callbackForAnonim();
          }
        }
      });

  /**
   * Функция для открытия логин попапа.
   * Важно, что достать ее можно из window и изменения не должны аффектить другие приложения. Будьте аккуратнее.
   */
  onLoginOpen = (params) => {
    if (params && params.additionalQueryParams) {
      this.loginPopupAdditionalQueryParams = params.additionalQueryParams;
    }
    this.loginPopupNext =
      (params && params.next && decodeURIComponent(params.next)) ||
      window.location.href;
    this.loginPopup = true;
  };

  onLoginClose = () => {
    this.loginPopup = false;
    this.loginPopupNext = null;
    this.loginPopupAdditionalQueryParams = {};
  };

  onRegistrationOpen = (params) => {
    if (params && params.additionalQueryParams) {
      this.registrationPopupAdditionalQueryParams =
        params.additionalQueryParams;
    }
    this.registrationPopupNext =
      (params && params.next) || window.location.href;
    this.registrationPopup = true;
  };

  onRegistrationClose = () => {
    this.registrationPopup = false;
    this.registrationPopupNext = null;
    this.registrationPopupAdditionalQueryParams = {};
  };

  onRegistrationSuccess = () => {
    window.location.reload();
  };

  onUserRegister = (baseUrl) => {
    const { header } = this.rootStore;

    const ga = window[window["GoogleAnalyticsObject"]];
    const gaMark = ga?.getAll()[0]?.get("linkerParam");

    if (typeof window.headerRegistartionLinkParams === "undefined") {
      window.headerRegistartionLinkParams = `?next=${encodeURIComponent(
        window.location.href
      )}`;
    }

    const gaDelimiter = window.headerRegistartionLinkParams ? "&" : "?";

    const utms = `${
      window.headerRegistartionLinkParams || gaMark ? "&" : "?"
    }utm_source=header&utm_campaign=new_header${
      header.isMainPage ? `&${MAIN_PAGE_QUERY_PARAM_STRING}` : ""
    }`;

    window.open(
      `https://id.${baseUrl || this.rootStore.baseUrl}/register/${
        window.headerRegistartionLinkParams || ""
      }${gaMark ? `${gaDelimiter}${gaMark}` : ""}${utms}`
    );
  };

  handleLoginPopupError = () => {
    window.location = `https://id.${
      this.rootStore.baseUrl
    }/login/?next=${encodeURIComponent(this.loginPopupNext)}`;
  };

  handleRegistrationPopupError = () => {
    window.location = `https://id.${
      this.rootStore.baseUrl
    }/register/?next=${encodeURIComponent(this.registrationPopupNext)}`;
  };

  setShowLoginButtons = (value) => {
    this.showLoginButtons = value;
  };

  /** Установить юзера */
  setUser = (user) => {
    this.user = user;

    this.profileHasBeenReceived = true;
  };

  get isPaidUser() {
    return this.isLoggedIn && this.user.is_paid_user;
  }

  /** Проверка на то, что юзер залогинен */
  get isLoggedIn() {
    return this.user && Object.keys(this.user).length !== 0;
  }

  /** Установить без профиля */
  setNoProfile = (noProfile) => {
    this.noProfile = noProfile;
  };

  get userId() {
    if (this.isLoggedIn && this.user) {
      return `${this.user.ati_code}${
        this.user.contact ? `_${this.user.contact.id}` : ""
      }`;
    }

    return "";
  }

  enableFeed = () => {
    callWindowFunction("getStoredValue", [
      LS_KEYS.IS_ACTIVE,
      ({ key, value }) => {
        this.rootStore.clientStorage.callbackIsActive({ key, value });
      },
    ]);
    callWindowFunction("subscribeToValue", [
      LS_KEYS.WEBPUSH,
      ({ key, value }) => {
        this.rootStore.clientStorage.callbackWebpush({ key, value });
      },
    ]);
    callWindowFunction("getStoredValue", [
      LS_KEYS.WEBPUSH,
      ({ key, value }) => {
        this.rootStore.clientStorage.callbackWebpush({ key, value });
      },
    ]);
    callWindowFunction("getStoredValue", [
      LS_KEYS.INVITE_TIME,
      ({ key, value }) => {
        if (!value) {
          this.rootStore.clientStorage.setNeedToShowInvite(true);
        }
        this.rootStore.clientStorage.callbackInvite({ key, value });
      },
    ]);
  };
}

export default Profile;
